import React, { useEffect, useState, useCallback } from "react";
import { Navigate, NavLink, Route, Routes } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Package, ShoppingBag, Ticket, Layers, Radio, Filter, FileText, ClipboardList, Users, Star, Mail, Handshake, Gem, Flame, Cake } from "lucide-react";
import AdminProducts from "@/pages/admin/AdminProducts";
import AdminOrders from "@/pages/admin/AdminOrders";
import AdminCoupons from "@/pages/admin/AdminCoupons";
import AdminTiers from "@/pages/admin/AdminTiers";
import AdminSchedule from "@/pages/admin/AdminSchedule";
import AdminFilters from "@/pages/admin/AdminFilters";
import AdminContent from "@/pages/admin/AdminContent";
import AdminPackBreaks from "@/pages/admin/AdminPackBreaks";
import AdminCustomers from "@/pages/admin/AdminCustomers";
import AdminReviews from "@/pages/admin/AdminReviews";
import AdminNewsletter from "@/pages/admin/AdminNewsletter";
import AdminOrderSlip from "@/pages/admin/AdminOrderSlip";
import AdminAffiliates from "@/pages/admin/AdminAffiliates";
import AdminCardSubmissions from "@/pages/admin/AdminCardSubmissions";
import AdminBigHits from "@/pages/admin/AdminBigHits";
import AdminBirthdays from "@/pages/admin/AdminBirthdays";

const TABS = [
  { to: "/admin/products", label: "Products", Icon: Package },
  { to: "/admin/filters", label: "Filters", Icon: Filter },
  { to: "/admin/content", label: "Site Content", Icon: FileText },
  { to: "/admin/reviews", label: "Reviews", Icon: Star },
  { to: "/admin/big-hits", label: "Big Hits", Icon: Flame },
  { to: "/admin/pack-breaks", label: "Pack Breaks", Icon: ClipboardList },
  { to: "/admin/orders", label: "Orders", Icon: ShoppingBag },
  { to: "/admin/customers", label: "Customers", Icon: Users },
  { to: "/admin/birthdays", label: "Birthdays", Icon: Cake },
  { to: "/admin/newsletter", label: "Newsletter", Icon: Mail },
  { to: "/admin/affiliates", label: "Affiliates", Icon: Handshake },
  { to: "/admin/card-submissions", label: "Card Submissions", Icon: Gem },
  { to: "/admin/coupons", label: "Coupons", Icon: Ticket },
  { to: "/admin/tiers", label: "Tiers", Icon: Layers },
  { to: "/admin/schedule", label: "Schedule", Icon: Radio },
];

export default function Admin() {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-16 text-zinc-500">Loading…</div>;
  if (!user) return <Navigate to="/login?next=/admin" replace />;
  if (!user.is_admin) return (
    <div className="max-w-md mx-auto p-16 text-center" data-testid="admin-denied">
      <h1 className="font-display text-3xl">Admins only</h1>
      <p className="text-zinc-400 mt-3">Your account ({user.email}) does not have admin access.</p>
    </div>
  );

  // Packing slip is a standalone print-ready page — render without the admin chrome.
  return (
    <Routes>
      <Route path="orders/:oid/slip" element={<AdminOrderSlip />} />
      <Route path="*" element={<AdminShell />} />
    </Routes>
  );
}

function AdminShell() {
  return (
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-8 grid md:grid-cols-[220px_1fr] gap-8">
      <aside className="space-y-1">
        <div className="text-label text-zinc-500 mb-4">Card Lobster · Admin</div>
        {TABS.map(({ to, label, Icon }) => (
          <NavLink
            key={to}
            to={to}
            data-testid={`admin-nav-${label.toLowerCase()}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-3 rounded-md transition-colors ${
                isActive ? "bg-[var(--cl-primary)] text-white" : "text-zinc-300 hover:bg-white/5"
              }`
            }
          >
            <Icon size={16} />
            <span className="text-sm">{label}</span>
          </NavLink>
        ))}
      </aside>
      <main>
        <Routes>
          <Route index element={<Navigate to="/admin/products" replace />} />
          <Route path="products" element={<AdminProducts />} />
          <Route path="filters" element={<AdminFilters />} />
          <Route path="content" element={<AdminContent />} />
          <Route path="reviews" element={<AdminReviews />} />
          <Route path="big-hits" element={<AdminBigHits />} />
          <Route path="pack-breaks" element={<AdminPackBreaks />} />
          <Route path="orders" element={<AdminOrders />} />
          <Route path="customers" element={<AdminCustomers />} />
          <Route path="birthdays" element={<AdminBirthdays />} />
          <Route path="newsletter" element={<AdminNewsletter />} />
          <Route path="affiliates" element={<AdminAffiliates />} />
          <Route path="card-submissions" element={<AdminCardSubmissions />} />
          <Route path="coupons" element={<AdminCoupons />} />
          <Route path="tiers" element={<AdminTiers />} />
          <Route path="schedule" element={<AdminSchedule />} />
        </Routes>
      </main>
    </div>
  );
}
